import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';
import { useAuth } from './AuthContext';

// Subscription tier limits
const TIER_LIMITS = {
  free: { products: 3, machines: 1, fonts: 5, downloadsPerMonth: 10 },
  starter: { products: 20, machines: Infinity, fonts: 20, downloadsPerMonth: 100 },
  pro: { products: Infinity, machines: Infinity, fonts: 50, downloadsPerMonth: Infinity },
  business: { products: Infinity, machines: Infinity, fonts: Infinity, downloadsPerMonth: Infinity },
  admin: { products: Infinity, machines: Infinity, fonts: Infinity, downloadsPerMonth: Infinity },
} as const;

type SubscriptionTier = keyof typeof TIER_LIMITS;

interface TierLimits {
  products: number;
  machines: number;
  fonts: number;
  downloadsPerMonth: number;
}

interface Workspace {
  id: string;
  name: string;
  ownerId: string;
  subscriptionTier: SubscriptionTier;
  polarSubscriptionId?: string;
  polarCustomerId?: string;
  cancelAt?: string | null; // ISO timestamp — when paid subscription access ends after cancellation
  createdAt: string;
  updatedAt: string;
}

interface UsageInfo {
  downloadCount: number;
  period: string;
}

interface WorkspaceContextValue {
  workspace: Workspace | null;
  isLoading: boolean;
  error: string | null;
  tier: SubscriptionTier;
  tierLimits: TierLimits;
  usage: UsageInfo | null;
  refreshWorkspace: () => Promise<void>;
  getTierLimits: (tier: SubscriptionTier) => TierLimits;
  isWithinLimit: (resource: keyof TierLimits, currentCount: number) => boolean;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

// Initialize Supabase client
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

function getCurrentPeriod(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [usage, setUsage] = useState<UsageInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const tier: SubscriptionTier = workspace?.subscriptionTier ?? 'free';
  const tierLimits = TIER_LIMITS[tier];

  const getTierLimits = useCallback((t: SubscriptionTier): TierLimits => {
    return TIER_LIMITS[t];
  }, []);

  const isWithinLimit = useCallback((resource: keyof TierLimits, currentCount: number): boolean => {
    const limit = tierLimits[resource];
    if (limit === Infinity) return true;
    return currentCount < limit;
  }, [tierLimits]);

  const fetchWorkspace = useCallback(async () => {
    if (!user) {
      setWorkspace(null);
      setUsage(null);
      setIsLoading(false);
      return;
    }

    try {
      setError(null);

      // Find workspace where user is a member
      const { data: memberData, error: memberError } = await supabase
        .from('workspace_members')
        .select('workspace_id')
        .eq('polar_user_id', user.id)
        .limit(1)
        .single();

      if (memberError && memberError.code !== 'PGRST116') {
        throw memberError;
      }

      if (!memberData) {
        // No workspace found — create one with free tier
        const { data: newWorkspace, error: createError } = await supabase
          .from('workspaces')
          .insert({
            name: `${user.name || user.email}'s Workspace`,
            owner_id: user.id,
            subscription_tier: 'free',
          })
          .select()
          .single();

        if (createError) throw createError;

        // Add user as owner member
        await supabase
          .from('workspace_members')
          .insert({
            workspace_id: newWorkspace.id,
            polar_user_id: user.id,
            email: user.email,
            display_name: user.name || user.email,
            role: 'owner',
          });

        setWorkspace({
          id: newWorkspace.id,
          name: newWorkspace.name,
          ownerId: newWorkspace.owner_id,
          subscriptionTier: newWorkspace.subscription_tier as SubscriptionTier,
          polarSubscriptionId: newWorkspace.polar_subscription_id,
          polarCustomerId: newWorkspace.polar_customer_id,
          cancelAt: newWorkspace.cancel_at,
          createdAt: newWorkspace.created_at,
          updatedAt: newWorkspace.updated_at,
        });
      } else {
        // Fetch existing workspace
        const { data: wsData, error: wsError } = await supabase
          .from('workspaces')
          .select('*')
          .eq('id', memberData.workspace_id)
          .single();

        if (wsError) throw wsError;

        setWorkspace({
          id: wsData.id,
          name: wsData.name,
          ownerId: wsData.owner_id,
          subscriptionTier: wsData.subscription_tier as SubscriptionTier,
          polarSubscriptionId: wsData.polar_subscription_id,
          polarCustomerId: wsData.polar_customer_id,
          cancelAt: wsData.cancel_at,
          createdAt: wsData.created_at,
          updatedAt: wsData.updated_at,
        });
      }

      // Fetch usage for current period
      const period = getCurrentPeriod();
      const workspaceId = memberData?.workspace_id || workspace?.id;
      if (workspaceId) {
        const { data: usageData } = await supabase
          .from('usage_tracking')
          .select('download_count, period')
          .eq('workspace_id', workspaceId)
          .eq('period', period)
          .maybeSingle();

        setUsage(usageData ? {
          downloadCount: usageData.download_count,
          period: usageData.period,
        } : { downloadCount: 0, period });
      }
    } catch (err) {
      console.error('[Workspace] Failed to fetch workspace:', err);
      setError(err instanceof Error ? err.message : 'Failed to load workspace');
    } finally {
      setIsLoading(false);
    }
  }, [user, workspace?.id]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchWorkspace();
    } else {
      setWorkspace(null);
      setUsage(null);
      setIsLoading(false);
    }
  }, [isAuthenticated, fetchWorkspace]);

  return (
    <WorkspaceContext.Provider
      value={{
        workspace,
        isLoading,
        error,
        tier,
        tierLimits,
        usage,
        refreshWorkspace: fetchWorkspace,
        getTierLimits,
        isWithinLimit,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}

export { TIER_LIMITS };
export type { SubscriptionTier, TierLimits, Workspace, UsageInfo };
